import type { Payout } from '../types/payout';

// ANTIGAS FONCTIONS (pour les achats, pas mining)
export const calculateMinimumSalePrice = (averagePurchasePrice: number, electricityCost: number = 0): number => {
  // Prix minimum = prix d'achat moyen + coût électrique + marge minimale (5%)
  return (averagePurchasePrice + electricityCost) * 1.05;
};

export const calculateMinimumSalePriceWithBNCCharges = (
  averagePurchasePrice: number,
  electricityCost: number = 0,
  bncChargesRate: number = 0.34 // 34% charges sociales + impôts
): number => {
  // Prix minimum = (prix d'achat / (1 - taux BNC)) + coût électrique
  // Le prix d'achat doit être grossi pour compenser les charges BNC
  // Le coût électrique s'ajoute car il est déductible
  return averagePurchasePrice / (1 - bncChargesRate) + electricityCost;
};

// NOUVELLES FONCTIONS POUR LE MINING
export const calculateMinimumSalePriceForMining = (
  averagePayoutValue: number, // Valeur moyenne au payout (base BNC)
  electricityCostPerToken: number = 0,
  bncRate: number = 0.34 // Taux BNC configurable
): number => {
  // Prix minimal pour être rentable après minage
  // Coût réel d'acquisition = 0€ (minage) + électricité
  // BNC = taxe à payer en cash sur la valeur au payout
  // Coût total réel = électricité + BNC payé

  const bncCostPerToken = averagePayoutValue * bncRate;
  const totalCostPerToken = electricityCostPerToken + bncCostPerToken;

  // Prix minimal = coût total + 5% marge
  const minimumPrice = totalCostPerToken * 1.05;

  return minimumPrice;
};

export const calculateMinimumSalePriceForMiningWithFlatTax = (
  averagePayoutValue: number, // Valeur moyenne au payout
  currentPrice: number, // Prix actuel du token
  electricityCostPerToken: number = 0,
  bncRate: number = 0.34, // Taux BNC configurable
  flatTaxRate: number = 0.314 // 31.4% flat tax (depuis janvier 2026)
): number => {
  // Pour le mining, le BNC se base sur la valeur au payout
  // Mais si averagePayoutValue = 0, on utilise le prix actuel pour avoir une base de calcul
  const bncBaseValue = averagePayoutValue > 0 ? averagePayoutValue : currentPrice;
  const bncAmount = bncBaseValue * bncRate;

  // Vérifier s'il y a une plus-value réelle
  const hasCapitalGain = currentPrice > averagePayoutValue;

  if (!hasCapitalGain) {
    // Pas de plus-value = pas de flat taxe
    // Prix minimal = électricité + BNC + 5% marge (identique au premier cas)
    return (electricityCostPerToken + bncAmount) * 1.05;
  }

  // Il y a une plus-value, inclure la flat tax
  // Si on vend à P, la plus-value est (P - averagePayoutValue)
  // La flat tax sera (P - averagePayoutValue) × flatTaxRate
  // On veut : P - flat_tax = électricité + BNC + 5% marge
  // P - (P - averagePayoutValue) × flatTaxRate = (électricité + BNC) × 1.05
  // P × (1 - flatTaxRate) + averagePayoutValue × flatTaxRate = (électricité + BNC) × 1.05
  // P = ((électricité + BNC) × 1.05 - averagePayoutValue × flatTaxRate) / (1 - flatTaxRate)

  const baseCostWithMargin = (electricityCostPerToken + bncAmount) * 1.05;
  const minimumPrice = (baseCostWithMargin - averagePayoutValue * flatTaxRate) / (1 - flatTaxRate);

  return Math.max(minimumPrice, averagePayoutValue); // Ne peut pas être inférieur à la valeur payout
};

// Calcul du gain net réel incluant tous les coûts et taxes
export const calculateRealProfitLoss = (
  currentTotalValue: number,
  totalValueAtPurchase: number,
  electricityCost: number = 0,
  flatTaxRate: number = 0.314 // 31.4% flat tax par défaut (depuis janvier 2026)
): { netProfit: number; netProfitPercentage: number } => {
  // Total investi = achat + électricité + autres coûts
  const totalInvested = totalValueAtPurchase + electricityCost;

  // Gain brut = valeur actuelle - total investi
  const grossProfit = currentTotalValue - totalInvested;

  // Flat tax (seulement sur le gain positif)
  const flatTax = grossProfit > 0 ? grossProfit * flatTaxRate : 0;

  // Gain net = gain brut - flat tax
  const netProfit = grossProfit - flatTax;

  // Pourcentage par rapport au total investi
  const netProfitPercentage = totalInvested > 0 ? (netProfit / totalInvested) * 100 : 0;

  return {
    netProfit,
    netProfitPercentage,
  };
};

// NEW: Calculate minimum sale price for mixed payout+achat transactions
export const calculateMinimumSalePriceForMixed = (
  payouts: Payout[],
  electricityCost: number = 0,
  bncRate: number = 0.34
): number => {
  if (payouts.length === 0) return 0;

  // Separate payout and achat transactions
  const payoutTypeTransactions = payouts.filter((p) => p.type === 'payout' || !p.type);
  const achatTypeTransactions = payouts.filter((p) => p.type === 'achat');

  // BNC only on mined coins (payout type)
  const bncAmount = payoutTypeTransactions.reduce((total, p) => total + ((p.totalValue || 0) * bncRate), 0);

  // Sum of all achat transactions
  const achatSum = achatTypeTransactions.reduce((total, p) => total + (p.totalValue || 0), 0);

  // Total coins (mined + purchased)
  const totalCoins = payouts.reduce((total, p) => total + (p.amount || 0), 0);

  if (totalCoins === 0) return 0;

  // Formula: (BNC_coins_minés + coût_électrique + somme_achats) * 1.05 / nombre_total_coins
  const minimumPrice = ((bncAmount + electricityCost + achatSum) * 1.05) / totalCoins;

  return minimumPrice;
};
